import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Avatar, Badge, fmtPKR, MONTHS } from '../components/ui.jsx';

const STATUS_CLASS = { PRESENT: 'present', ABSENT: 'absent', HALF_DAY: 'half-day', LEAVE: 'no-data' };

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const now = new Date();
  const [emp, setEmp] = useState(null);
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [attendance, setAttendance] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => { api(`/employees/${id}`).then(setEmp).catch((e) => setError(e.message)); }, [id]);
  useEffect(() => {
    api(`/attendance/employee/${id}/${ym.year}/${ym.month}`).then(setAttendance).catch(() => setAttendance([]));
  }, [id, ym]);

  if (error) return <div className="empty-state"><p>{error}</p></div>;
  if (!emp) return <div className="empty-state"><p>Loading profile…</p></div>;

  const byDate = Object.fromEntries(attendance.map((r) => [r.date, r]));
  const daysInMonth = new Date(ym.year, ym.month, 0).getDate();
  const firstDow = new Date(Date.UTC(ym.year, ym.month - 1, 1)).getUTCDay();
  const stats = attendance.reduce((s, r) => {
    if (r.status === 'PRESENT') s.present++;
    else if (r.status === 'ABSENT') s.absent++;
    else if (r.status === 'HALF_DAY') s.half++;
    s.ot += r.overtimeHours;
    return s;
  }, { present: 0, absent: 0, half: 0, ot: 0 });

  const prevMonth = () => setYm(({ year, month }) => month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 });
  const nextMonth = () => setYm(({ year, month }) => month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 });

  const outstanding = emp.advances
    .filter((a) => a.status === 'APPROVED')
    .reduce((s, a) => s + Number(a.amount) - Number(a.amountRepaid), 0);

  return (
    <>
      <div className="page-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
      </div>

      <div className="dash-grid" style={{ alignItems: 'start' }}>
        <div className="card">
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
            <Avatar name={emp.name} id={emp.code} size={64} />
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>{emp.name}</h2>
              <div className="text-muted">{emp.designation} · {emp.code}</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Badge kind={emp.status === 'ACTIVE' ? 'active' : 'absent'}>{emp.status}</Badge>
            </div>
          </div>
          <div className="profile-grid">
            <div className="profile-field"><label>Site</label><span>{emp.site.name}</span></div>
            <div className="profile-field"><label>Department</label><span>{emp.department.name}</span></div>
            <div className="profile-field"><label>Employment</label>
              <span>{emp.employmentType.name}{emp.contractEndDate ? ` · ends ${emp.contractEndDate.slice(0, 10)}` : ''}</span></div>
            <div className="profile-field"><label>Daily Rate</label><span>{fmtPKR(emp.dailyRate)}</span></div>
            <div className="profile-field"><label>Joined</label><span>{new Date(emp.joinedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
            <div className="profile-field"><label>Phone</label><span>{emp.phone || '—'}</span></div>
            <div className="profile-field"><label>CNIC</label><span>{emp.cnic || '—'}</span></div>
          </div>

          <div className="divider" />
          <div className="card-title">Advances <span>· outstanding {fmtPKR(outstanding)}</span></div>
          {emp.advances.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No advances recorded.</div>}
          {emp.advances.map((a) => (
            <div key={a.id} className="payslip-row">
              <span className="lbl">{new Date(a.requestedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {a.reason || 'Advance'}</span>
              <span className="val">{fmtPKR(a.amount)} <Badge kind={a.status === 'APPROVED' ? 'active' : a.status === 'SETTLED' ? 'administrative' : a.status === 'PENDING' ? 'half-day' : 'absent'}>{a.status}</Badge></span>
            </div>
          ))}

          {emp.incidents.length > 0 && (
            <>
              <div className="divider" />
              <div className="card-title">Incidents & Warnings <span>· {emp.incidents.length} on record</span></div>
              {emp.incidents.map((i) => (
                <div key={i.id} className="payslip-row">
                  <span className="lbl">{new Date(i.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {i.title}</span>
                  <span className="val"><Badge kind={i.severity}>{i.severity}</Badge></span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="card">
          <div className="cal-header">
            <button className="btn btn-ghost btn-sm" onClick={prevMonth}>‹</button>
            <div style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>{MONTHS[ym.month - 1]} {ym.year}</div>
            <button className="btn btn-ghost btn-sm" onClick={nextMonth}>›</button>
          </div>
          <div className="cal-grid">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => <div key={d} className="cal-day-hdr">{d}</div>)}
            {Array.from({ length: firstDow }, (_, i) => <div key={'e' + i} className="cal-day empty" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const dateStr = `${ym.year}-${String(ym.month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
              const rec = byDate[dateStr];
              const dow = new Date(Date.UTC(ym.year, ym.month - 1, i + 1)).getUTCDay();
              const cls = rec ? STATUS_CLASS[rec.status] : dow === 0 ? 'sunday' : 'no-data';
              return (
                <div key={dateStr} className={`cal-day ${cls}`}
                  title={rec ? `${rec.status}${rec.overtimeHours ? ` · ${rec.overtimeHours}h OT` : ''}` : ''}>
                  {i + 1}
                </div>
              );
            })}
          </div>
          <div className="att-summary-bar" style={{ marginTop: 16, marginBottom: 0 }}>
            <div className="att-stat"><div className="att-stat-val text-green">{stats.present}</div><div className="att-stat-lbl">Present</div></div>
            <div className="att-stat"><div className="att-stat-val text-red">{stats.absent}</div><div className="att-stat-lbl">Absent</div></div>
            <div className="att-stat"><div className="att-stat-val text-amber">{stats.half}</div><div className="att-stat-lbl">Half-Day</div></div>
            <div className="att-stat"><div className="att-stat-val text-blue">{stats.ot}h</div><div className="att-stat-lbl">Overtime</div></div>
          </div>
        </div>
      </div>
    </>
  );
}
